const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Helper function to create fetch with timeout
const fetchWithTimeout = async (url, options = {}, timeout = 10000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

/* ==== Authentication ==== */

export const signUpUser = async (formData) => {
  console.log('Sign Up User:', formData);
  console.log("API_BASE_URL:", API_BASE_URL);
  try {
    const response = await fetch(`${API_BASE_URL}/users/sign-up/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    const responseData = await response.json();
    if (response.status === 201) {  
      return { success: true, data: responseData };
    } else if (response.status === 400) {
      return { success: false, message: responseData.error || 'Invalid input. Please check the data and try again.' };
    } else if (response.status === 409) {
      return { success: false, message: 'An account with this email already exists. Please use another email.' };
    } else {
      return { success: false, message: responseData.error || 'An unexpected error occurred. Please try again.' };
    }
  } catch (error) {
    return { success: false, message: `Error: ${error.message}` };
  }
};

export const loginUser = async (email, password) => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/users/token/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username: email, password: password }),
    }, 15000); // 15 second timeout

    const responseData = await response.json();

    if (response.status === 200) {
      return { success: true, data: responseData };
    } else if (response.status === 401) {
      return { success: false, message: 'Email or password is incorrect. Please try again.' };
    } else if (response.status === 400) {
      return { success: false, message: 'No account found with this email. Please check and try again.' };
    } else if (response.status === 409) {
      return { success: false, message: 'You are already logged in. Please log out to switch accounts.' };
    } else {
      return { success: false, message: responseData.error || 'An unexpected error occurred. Please try again.' };
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      return { success: false, message: 'Request timeout. Please check your connection and try again.' };
    }
    return { success: false, message: `Error: ${error.message}` };
  }
};

export const refreshUserToken = async (refreshUserToken) => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/users/token/refresh/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh: refreshUserToken }),
    }, 10000); // 10 second timeout

    if (response.status === 200) {
      const data = await response.json();
      return { success: true, data };
    } else {
      return { success: false, message: 'Failed to refresh token' };
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      return { success: false, message: 'Token refresh timeout' };
    }
    return { success: false, message: `Error: ${error.message}` };
  }
};

export const verifyUserEmail = async (uidb64, token) => {
  try {
    const response = await fetch(`${API_BASE_URL}/users/verify-email/${uidb64}/${token}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(errorResponse.message || 'Failed to verify email');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

/* ==== Reset Password ==== */

export const requestUserPasswordReset = async (email, csrftoken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/users/reset_password/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrftoken,
      },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(errorResponse.message || 'Failed to request password reset');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

export const checkUserPasswordResetToken = async (uidb64, token) => {
  try {
    const response = await fetch(`${API_BASE_URL}/users/reset/${uidb64}/${token}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to validate token');
    }

    return { success: result.success, message: result.message };
  } catch (error) {
    throw error;
  }
};

export const resetUserPassword = async (uidb64, token, password, csrftoken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/users/reset/${uidb64}/${token}/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrftoken,
      },
      credentials: 'include',
      body: JSON.stringify({ password }),
    });
    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to reset password');
    }

    return { success: result.success, message: result.message };
  } catch (error) {
    throw error;
  }
};
